import { useOverlay } from "@Light/components/page/Overlay";
import { useScaffold } from "@Light/scaffold";
import { clsx } from "clsx";
import { motion } from "motion/react";
import React from "react";
import { MenuHeaderTitle, useDefaultHeaderTitle } from "./MenuHeaderTitle";

export type MenuHeaderProps = React.ComponentProps<"header"> & {
  titleLink?: string;
  leftButton?: React.ReactNode;
  rightButton?: React.ReactNode;
  floating?: boolean;
  compact?: boolean;
};

const HeaderButton: React.FC<{
  children: React.ReactNode;
  floating?: boolean;
}> = ({ children, floating }) => {
  if (!children) return <div className="w-10 h-10" />;
  if (!floating) return <>{children}</>;
  return <div className="w-fit rounded-full bg-white/90">{children}</div>;
};

export const MenuHeader: React.FC<MenuHeaderProps> = React.forwardRef<
  HTMLElement,
  MenuHeaderProps
>(
  (
    {
      children,
      titleLink,
      leftButton,
      rightButton,
      floating,
      compact,
      className,
      ...props
    },
    ref,
  ) => {
    const scaffold = useScaffold();
    const Link = scaffold.system.link;
    const { isMinimalHeader } = scaffold.page.header;
    const { contentOpacity } = useOverlay();
    const fadeStyle = contentOpacity ? { opacity: contentOpacity } : undefined;
    const defaultTitle = useDefaultHeaderTitle();
    const hasTitle = Boolean(children ?? defaultTitle);

    if (compact) {
      return (
        <header
          {...props}
          ref={ref}
          className={clsx("px-4xl py-lg", className)}
        >
          <motion.div
            style={fadeStyle}
            className={clsx(
              "text-center text-md font-medium",
              scaffold.page.colors.text.tertiary,
            )}
          >
            {children}
          </motion.div>
        </header>
      );
    }

    let title: React.ReactNode = null;
    if (!floating && hasTitle) {
      title = <MenuHeaderTitle>{children}</MenuHeaderTitle>;
      if (titleLink) {
        title = (
          <Link className="items-center" to={titleLink}>
            {title}
          </Link>
        );
      }
    }

    return (
      <header
        {...props}
        ref={ref}
        className={clsx(
          floating
            ? "absolute inset-x-0 top-0 z-10 pt-6 px-3"
            : "pt-6 px-3 pb-4",
          !floating &&
            isMinimalHeader &&
            "sm:pb-2 sm:z-10 sm:shadow-[0_8px_8px_#fff]",
          !floating && !hasTitle && "z-10 shadow-[0_8px_8px_#fff]",
          className,
        )}
      >
        <div
          className={clsx(
            "flex justify-between w-full",
            floating ? "items-start" : "items-center",
          )}
        >
          <div className="w-1/6">
            <HeaderButton floating={floating}>{leftButton}</HeaderButton>
          </div>
          {title && (
            <motion.div
              style={fadeStyle}
              className={clsx(
                "w-2/3 flex justify-center",
                isMinimalHeader && "sm:hidden",
              )}
            >
              <div className="flex items-center justify-center">{title}</div>
            </motion.div>
          )}
          <div className="w-1/6 flex justify-end">
            <HeaderButton floating={floating}>{rightButton}</HeaderButton>
          </div>
        </div>
      </header>
    );
  },
);
